import { useId, useState } from 'react';
import { cx } from '../lib/cx';
import { validateSet, type SetFieldError } from '../lib/workout';
import { IconCheck } from '../components/icons';

/** Vidi `exerciseLoadTypes` u `@st/db` — sklek nema kilazu, plank nema ponavljanja. */
export type SetLoadType = 'weighted' | 'bodyweight' | 'timed';

export interface SetRowValue {
  reps: number | '';
  weightKg: number | '';
  durationSeconds?: number | '';
  done: boolean;
}

export interface SetRowLabels {
  set: string;
  reps: string;
  weight: string;
  addedWeight: string;
  addWeight: string;
  duration: string;
  markDone: string;
  invalidReps: string;
  invalidWeight: string;
  invalidDuration: string;
}

export interface SetRowProps {
  index: number;
  value: SetRowValue;
  onChange: (next: SetRowValue) => void;
  labels: SetRowLabels;
  loadType?: SetLoadType;
  targetHint?: string;
  showHeader?: boolean;
}

const FIELD_CLASS =
  'w-full rounded-sm border border-line bg-surface px-3 py-2 text-center text-[15px] text-ink focus:border-accent';

/**
 * Jedan red unosa u teretani.
 *
 * Sto se unosi ovisi o vjezbi, ne o ekranu: kod utega idu ponavljanja i kilaza,
 * kod vlastite tezine samo ponavljanja (dodatni uteg se otkriva na zahtjev, jer
 * zgib s pojasom postoji), a kod izdrzaja samo trajanje. Prije je svaka vjezba
 * dobivala polje za kilazu pa je kod skleka stajalo prazno polje bez znacenja.
 * Granice su iste kao u `setLogInput` iz `@st/contract`.
 */
export function SetRow({
  index,
  value,
  onChange,
  labels,
  loadType = 'weighted',
  targetHint,
  showHeader = false,
}: SetRowProps) {
  const hasAddedWeight = value.weightKg !== '' && value.weightKg !== 0;
  const [showWeight, setShowWeight] = useState(hasAddedWeight);

  const timed = loadType === 'timed';
  const bodyweight = loadType === 'bodyweight';
  // Kod vlastite tezine polje za kilazu postoji tek kad ga korisnik zatrazi.
  const weightVisible = loadType === 'weighted' || (bodyweight && (showWeight || hasAddedWeight));

  const error: SetFieldError = validateSet(value);
  /*
    `set-${index}` se ponavljao u svakoj vježbi na ekranu treninga — pet vježbi
    je davalo pet elemenata s id-om `set-0-reps`. `useId` daje jedinstven
    prefiks po retku, pa `aria-describedby` pokazuje na pravu poruku.
  */
  const idBase = `${useId()}-set-${index}`;
  const errorText =
    error === 'reps'
      ? labels.invalidReps
      : error === 'weight'
        ? labels.invalidWeight
        : error === 'duration'
          ? labels.invalidDuration
          : null;

  const columns = timed
    ? 'grid-cols-[3rem_1fr_2.75rem]'
    : weightVisible
      ? 'grid-cols-[3rem_1fr_1fr_2.75rem]'
      : bodyweight
        ? 'grid-cols-[3rem_1fr_auto_2.75rem]'
        : 'grid-cols-[3rem_1fr_1fr_2.75rem]';

  const weightLabel = bodyweight ? labels.addedWeight : labels.weight;

  return (
    <div className="flex flex-col gap-1">
      {showHeader && (
        <div className={cx('grid items-center gap-2 px-1', columns)}>
          <span className="eyebrow">{labels.set}</span>
          <span className="eyebrow">{timed ? labels.duration : labels.reps}</span>
          {!timed && <span className="eyebrow">{weightVisible ? weightLabel : ''}</span>}
          <span className="sr-only">{labels.markDone}</span>
        </div>
      )}
      <div
        data-testid={`set-row-${index}`}
        data-load-type={loadType}
        className={cx(
          'grid items-center gap-2 rounded-md border p-2',
          columns,
          value.done ? 'border-accent bg-accent-light' : 'border-line-subtle bg-surface',
          errorText && 'border-danger',
        )}
      >
        <span className="text-center font-display text-[18px] font-semibold text-ink">
          {index + 1}
        </span>

        {timed ? (
          <input
            id={`${idBase}-duration`}
            type="number"
            inputMode="numeric"
            min={0}
            max={28800}
            step={1}
            value={value.durationSeconds === '' || value.durationSeconds == null
              ? ''
              : String(value.durationSeconds)}
            placeholder={targetHint ?? ''}
            aria-label={`${labels.set} ${index + 1} — ${labels.duration}`}
            aria-invalid={error === 'duration' || undefined}
            aria-describedby={error === 'duration' ? `${idBase}-error` : undefined}
            onChange={(e) =>
              onChange({
                ...value,
                durationSeconds: e.target.value === '' ? '' : Number(e.target.value),
              })
            }
            className={FIELD_CLASS}
          />
        ) : (
          <input
            id={`${idBase}-reps`}
            type="number"
            inputMode="numeric"
            min={0}
            max={1000}
            step={1}
            value={value.reps === '' ? '' : String(value.reps)}
            placeholder={targetHint ?? ''}
            aria-label={`${labels.set} ${index + 1} — ${labels.reps}`}
            aria-invalid={error === 'reps' || undefined}
            aria-describedby={error === 'reps' ? `${idBase}-error` : undefined}
            onChange={(e) =>
              onChange({ ...value, reps: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={FIELD_CLASS}
          />
        )}

        {!timed &&
          (weightVisible ? (
            <input
              id={`${idBase}-weight`}
              type="number"
              inputMode="decimal"
              min={0}
              max={1000}
              step={0.5}
              value={value.weightKg === '' ? '' : String(value.weightKg)}
              aria-label={`${labels.set} ${index + 1} — ${weightLabel}`}
              aria-invalid={error === 'weight' || undefined}
              aria-describedby={error === 'weight' ? `${idBase}-error` : undefined}
              onChange={(e) =>
                onChange({ ...value, weightKg: e.target.value === '' ? '' : Number(e.target.value) })
              }
              className={FIELD_CLASS}
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowWeight(true)}
              className="whitespace-nowrap rounded-pill px-3 py-2 text-[12.5px] text-ink-muted transition-colors hover:bg-bg-alt hover:text-accent-dark"
            >
              {labels.addWeight}
            </button>
          ))}

        <button
          type="button"
          role="checkbox"
          aria-checked={value.done}
          aria-label={`${labels.markDone} ${index + 1}`}
          disabled={!!errorText}
          onClick={() => onChange({ ...value, done: !value.done })}
          className={cx(
            'flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
            value.done
              ? 'border-accent-dark bg-accent-dark text-surface'
              : 'border-line-strong bg-surface text-ink-muted hover:border-accent-dark',
            errorText && 'opacity-40 pointer-events-none',
          )}
        >
          <IconCheck width={18} height={18} />
        </button>
      </div>
      {/* Poruka visi o polju preko `aria-describedby` — ne stoji sama uz redak. */}
      {errorText && (
        <p id={`${idBase}-error`} role="alert" className="px-1 text-[12px] text-danger">
          {errorText}
        </p>
      )}
    </div>
  );
}
